import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    forwardRef,
} from "react";
import { Link } from "react-router-dom";
import {
    UploadCloud,
    File as FileIcon,
    Trash2,
    Download,
    Loader2,
    X,
    CheckCircle2,
    AlertCircle,
    ScanText,
    CalendarClock,
    Link2,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { crud, absoluteUrl, api } from "../lib/api";
import { VekaletUyariBanner, OcrDurumBadge } from "./VekaletMerkezPanel";

const DOSYA_TIPLERI = [
    { value: "PDF", label: "PDF" },
    { value: "IMAGE", label: "Resim" },
    { value: "OTHER", label: "Diğer" },
];

function inferDosyaTuru(file) {
    const type = String(file?.type || "").toLowerCase();
    const name = String(file?.name || "").toLowerCase();
    if (type.includes("pdf") || name.endsWith(".pdf")) return "PDF";
    if (type.startsWith("image/")) return "IMAGE";
    return "OTHER";
}

function KaynakBadge({ kaynak, sigortaNo }) {
    if (kaynak === "SIGORTA_EVRAK") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-violet-500/35 bg-violet-500/15 text-[10px] font-semibold text-violet-200">
                <Link2 className="h-3 w-3" />
                Sigorta evrak
                {sigortaNo ? ` · ${sigortaNo}` : ""}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-500/30 bg-slate-500/10 text-[10px] text-slate-400">
            Manuel
        </span>
    );
}

const VekaletDosyaSection = forwardRef(function VekaletDosyaSection(
    { parentId, onParentUpdated },
    ref,
) {
    const isQueueMode = !parentId;
    const [docs, setDocs] = useState([]);
    const [vekaletMeta, setVekaletMeta] = useState(null);
    const [loading, setLoading] = useState(!isQueueMode);
    const [pending, setPending] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [ocrBusyId, setOcrBusyId] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);
    const pendingRef = useRef(pending);

    useEffect(() => {
        pendingRef.current = pending;
    }, [pending]);

    const loadMeta = useCallback(async () => {
        if (!parentId) {
            setVekaletMeta(null);
            return;
        }
        try {
            const v = await crud.retrieve("vekaletler", parentId);
            setVekaletMeta(v);
        } catch {
            setVekaletMeta(null);
        }
    }, [parentId]);

    const loadDocs = useCallback(async () => {
        if (!parentId) {
            setDocs([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await crud.list("vekalet-dosyalari", {
                vekalet: parentId,
                page_size: 200,
            });
            const list = Array.isArray(data) ? data : data?.results || [];
            setDocs(
                list.filter((d) => {
                    const fk = d?.vekalet;
                    if (typeof fk === "object") {
                        return String(fk?.id) === String(parentId);
                    }
                    return String(fk) === String(parentId);
                }),
            );
            await loadMeta();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Vekalet dosyaları yüklenemedi.",
            );
            setDocs([]);
        } finally {
            setLoading(false);
        }
    }, [parentId, loadMeta]);

    useEffect(() => {
        loadDocs();
    }, [loadDocs]);

    function addFiles(fileList) {
        const arr = Array.from(fileList || []);
        if (!arr.length) return;
        setPending((prev) => [
            ...prev,
            ...arr.map((file) => ({
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                file,
                dosya_tipi: inferDosyaTuru(file),
                progress: 0,
                done: false,
                error: null,
            })),
        ]);
    }

    function updatePending(id, patch) {
        setPending((arr) => arr.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    }

    function removePending(id) {
        setPending((arr) => arr.filter((p) => p.id !== id));
    }

    async function uploadOne(item, parentIdOverride) {
        const pid = parentIdOverride ?? parentId;
        if (!pid) {
            const msg = "Önce vekalet kaydı oluşturulmalı.";
            updatePending(item.id, { error: msg });
            return { ok: false, msg };
        }
        try {
            updatePending(item.id, { progress: 1, error: null });
            await crud.upload(
                "vekalet-dosyalari",
                {
                    vekalet: pid,
                    dosya: item.file,
                    dosya_tipi: item.dosya_tipi || "OTHER",
                },
                (e) => {
                    if (!e?.total) return;
                    const pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
                    updatePending(item.id, { progress: pct });
                },
            );
            updatePending(item.id, { progress: 100, done: true, error: null });
            return { ok: true };
        } catch (e) {
            const msg =
                e?.response?.data?.detail || e?.message || "Yükleme başarısız.";
            updatePending(item.id, { error: msg, done: false });
            return { ok: false, msg };
        }
    }

    async function uploadAll() {
        if (!pending.length || isQueueMode) return;
        setUploading(true);
        let success = 0;
        let failed = 0;
        for (const item of pending) {
            if (item.done) continue;
            const r = await uploadOne(item);
            if (r.ok) success += 1;
            else failed += 1;
        }
        setUploading(false);
        if (success > 0) {
            setPending((arr) => arr.filter((p) => !p.done));
            await loadDocs();
            await onParentUpdated?.(parentId);
            toast.success(
                `${success} dosya işlendi. OCR ile tarihler otomatik güncellendi.`,
            );
        }
        if (failed > 0 && success === 0) {
            toast.error("Dosya yüklemesi başarısız. Hata satırlarını kontrol edin.");
        }
    }

    useImperativeHandle(
        ref,
        () => ({
            hasQueue: () => pendingRef.current.some((p) => !p.done && !p.error),
            count: () => pendingRef.current.filter((p) => !p.done).length,
            flushQueue: async (newParentId) => {
                if (!newParentId) return { success: 0, failed: 0 };
                const items = [...pendingRef.current].filter((p) => !p.done);
                let success = 0;
                let failed = 0;
                for (const item of items) {
                    const r = await uploadOne(item, newParentId);
                    if (r.ok) success += 1;
                    else failed += 1;
                }
                if (success > 0) {
                    setPending((arr) => arr.filter((p) => !p.done));
                    await onParentUpdated?.(newParentId);
                }
                return { success, failed };
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [parentId],
    );

    async function deleteDoc(doc) {
        if (!window.confirm(`"${doc?.dosya || "Dosya"}" silinsin mi?`)) return;
        try {
            await crud.remove("vekalet-dosyalari", doc.id);
            await loadDocs();
            toast.success("Dosya silindi.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Silme işlemi başarısız.");
        }
    }

    async function runOcr(doc, force = false) {
        setOcrBusyId(doc.id);
        try {
            const r = await api.post(
                `/v1/admin/vekalet-dosyalari/${doc.id}/ocr-tara/`,
                { force: force ? "1" : "0" },
            );
            if (r.data?.ok) {
                toast.success("OCR tamam — tarihler güncellendi.");
            } else {
                toast.error(r.data?.error || "OCR sonuç vermedi.");
            }
            await loadDocs();
            await onParentUpdated?.(parentId);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.response?.data?.error || "OCR başarısız");
        } finally {
            setOcrBusyId(null);
        }
    }

    async function runOcrAll() {
        if (!parentId) return;
        setOcrBusyId("all");
        try {
            const r = await api.post(`/v1/admin/vekaletler/${parentId}/ocr-tara/`);
            toast.success(
                `OCR: ${r.data?.basarili || 0} başarılı, ${r.data?.hatali || 0} hatalı`,
            );
            await loadDocs();
            await onParentUpdated?.(parentId);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toplu OCR başarısız");
        } finally {
            setOcrBusyId(null);
        }
    }

    const sigortaKaynak = docs.filter((d) => d.kaynak === "SIGORTA_EVRAK").length;
    const manuelKaynak = docs.length - sigortaKaynak;

    return (
        <div className="md:col-span-2 mt-2 rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/8 via-[#0B0F19]/95 to-[#121A2F]/90 overflow-hidden shadow-xl shadow-black/20">
            <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-r from-violet-500/12 via-[#D4AF37]/8 to-transparent">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center shrink-0">
                            <ShieldCheck className="h-5 w-5 text-violet-300" />
                        </div>
                        <div>
                            <div className="text-base font-bold text-slate-100 flex items-center gap-2">
                                Vekalet Dosyaları
                                <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                            </div>
                            <div className="text-xs text-slate-500 mt-1 max-w-xl">
                                Sigorta dosyasına yüklenen vekalet evrakları buraya otomatik
                                aktarılır. OCR ile başlangıç/bitiş tarihleri yakalanır.
                            </div>
                            {vekaletMeta && (
                                <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                                    <span className="px-2 py-0.5 rounded border border-emerald-500/30 text-emerald-300">
                                        {vekaletMeta.durum_display || vekaletMeta.durum}
                                    </span>
                                    {vekaletMeta.baslangic_tarihi && (
                                        <span className="text-slate-500">
                                            Başlangıç: {vekaletMeta.baslangic_tarihi}
                                        </span>
                                    )}
                                    {vekaletMeta.bitis_tarihi && (
                                        <span className="text-slate-500">
                                            Bitiş: {vekaletMeta.bitis_tarihi}
                                        </span>
                                    )}
                                    {vekaletMeta.kalan_gun != null && (
                                        <span
                                            className={
                                                vekaletMeta.kalan_gun <= 7
                                                    ? "text-amber-400 font-semibold"
                                                    : "text-slate-500"
                                            }
                                        >
                                            {vekaletMeta.kalan_gun} gün kaldı
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="h-9 px-3 rounded-lg border border-[#D4AF37]/35 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#F3E5AB] text-xs font-semibold inline-flex items-center gap-2"
                    >
                        <UploadCloud className="h-4 w-4" />
                        Dosya Ekle
                    </button>
                    {parentId && docs.some((d) => d.ocr_durumu !== "TAMAMLANDI") && (
                        <button
                            type="button"
                            disabled={!!ocrBusyId}
                            onClick={runOcrAll}
                            className="h-9 px-3 rounded-lg border border-violet-500/35 bg-violet-600/80 hover:bg-violet-600 text-white text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {ocrBusyId === "all" ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <ScanText className="h-4 w-4" />
                            )}
                            OCR Tara
                        </button>
                    )}
                </div>
            </div>

            {vekaletMeta?.uyari && <VekaletUyariBanner uyari={vekaletMeta.uyari} />}

            {!isQueueMode && docs.length > 0 && (
                <div className="px-5 py-3 grid grid-cols-3 gap-2 border-b border-white/5">
                    <MiniStat label="Toplam" value={docs.length} />
                    <MiniStat label="Sigorta evrak" value={sigortaKaynak} tone="violet" />
                    <MiniStat label="Manuel" value={manuelKaynak} tone="slate" />
                </div>
            )}

            <div className="p-4">
                <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,image/*"
                    multiple
                    onChange={(e) => addFiles(e.target.files)}
                />

                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        addFiles(e.dataTransfer?.files || []);
                    }}
                    className={`rounded-xl border-2 border-dashed p-5 text-center text-xs transition ${
                        dragActive
                            ? "border-violet-400 bg-violet-500/10"
                            : "border-white/10 bg-[#121A2F]/60"
                    }`}
                >
                    PDF veya resim dosyalarını sürükleyin — sigorta evrakından gelenler otomatik
                    işaretlenir
                </div>

                {pending.length > 0 && (
                    <div className="mt-3 rounded-xl border border-white/10 bg-[#121A2F]">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                            <div className="text-xs font-semibold text-slate-300">
                                Bekleyen ({pending.filter((p) => !p.done).length})
                            </div>
                            {!isQueueMode && (
                                <button
                                    type="button"
                                    disabled={uploading}
                                    onClick={uploadAll}
                                    className="h-8 px-3 rounded-md bg-violet-600 text-white text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-2"
                                >
                                    {uploading ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <UploadCloud className="h-3.5 w-3.5" />
                                    )}
                                    Yükle
                                </button>
                            )}
                        </div>
                        <div className="max-h-48 overflow-auto divide-y divide-white/5">
                            {pending.map((p) => (
                                <div key={p.id} className="px-3 py-2 flex items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs truncate">{p.file?.name}</div>
                                        {p.error && (
                                            <div className="text-[10px] text-red-400">{p.error}</div>
                                        )}
                                    </div>
                                    <button type="button" onClick={() => removePending(p.id)}>
                                        <X className="h-4 w-4 text-slate-500" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                        Kayıtlı dosyalar
                    </div>
                    {loading ? (
                        <div className="py-6 text-center text-xs text-slate-500">
                            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                            Yükleniyor…
                        </div>
                    ) : docs.length === 0 ? (
                        <div className="py-8 text-center rounded-xl border border-dashed border-white/10 text-xs text-slate-500">
                            Henüz dosya yok. Sigorta dosyasına vekalet yüklediğinizde burada
                            görünür.
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2">
                            {docs.map((d) => {
                                const fname =
                                    String(d?.dosya || "").split("/").pop() || "Dosya";
                                const sigortaId = d?.kaynak_sigorta_dosya;
                                return (
                                    <article
                                        key={d.id}
                                        className="rounded-xl border border-white/10 bg-[#0B0F19]/80 p-3 hover:border-violet-500/30 transition"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <FileIcon className="h-4 w-4 text-violet-400 shrink-0" />
                                                    <span className="text-xs font-medium text-slate-100 truncate">
                                                        {fname}
                                                    </span>
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    <KaynakBadge
                                                        kaynak={d.kaynak}
                                                        sigortaNo={d.kaynak_sigorta_dosya_no}
                                                    />
                                                    <OcrDurumBadge
                                                        durum={d.ocr_durumu}
                                                        hata={d.ocr_hata}
                                                    />
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400">
                                                        {d.dosya_tipi_display || d.dosya_tipi}
                                                    </span>
                                                </div>
                                                {d.ocr_durumu !== "TAMAMLANDI" && (
                                                    <button
                                                        type="button"
                                                        disabled={ocrBusyId === d.id}
                                                        onClick={() => runOcr(d)}
                                                        className="mt-2 text-[10px] text-violet-300 hover:underline inline-flex items-center gap-1"
                                                    >
                                                        {ocrBusyId === d.id ? (
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <ScanText className="h-3 w-3" />
                                                        )}
                                                        OCR ile tarih oku
                                                    </button>
                                                )}
                                                <div className="mt-1 text-[10px] text-slate-600">
                                                    {d?.yuklenme_tarihi
                                                        ? new Date(
                                                              d.yuklenme_tarihi,
                                                          ).toLocaleString("tr-TR")
                                                        : ""}
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <a
                                                    href={absoluteUrl(
                                                        d?.dosya_url || d?.dosya,
                                                    )}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="h-8 w-8 rounded-lg border border-white/10 hover:bg-white/5 inline-flex items-center justify-center"
                                                    title="İndir"
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={() => deleteDoc(d)}
                                                    className="h-8 w-8 rounded-lg border border-white/10 hover:bg-red-500/10 inline-flex items-center justify-center"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                                                </button>
                                            </div>
                                        </div>
                                        {sigortaId && (
                                            <Link
                                                to={`/e/sigorta-dosyalari/${sigortaId}`}
                                                className="mt-2 inline-flex items-center gap-1 text-[10px] text-violet-300 hover:underline"
                                            >
                                                <Link2 className="h-3 w-3" />
                                                Kaynak sigorta dosyası
                                            </Link>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="mt-4 px-3 py-2.5 rounded-lg bg-violet-500/10 border border-violet-500/20 text-[11px] text-violet-200 inline-flex items-center gap-2 w-full">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                    OCR sonrası vekalet başlangıç/bitiş tarihleri ve kalan gün uyarıları otomatik
                    güncellenir.
                </div>
            </div>
        </div>
    );
});

function MiniStat({ label, value, tone = "gold" }) {
    const cls =
        tone === "violet"
            ? "border-violet-500/25 bg-violet-500/10 text-violet-200"
            : tone === "slate"
              ? "border-white/10 bg-white/5 text-slate-400"
              : "border-[#D4AF37]/25 bg-[#D4AF37]/10 text-[#F3E5AB]";
    return (
        <div className={`rounded-lg border px-3 py-2 text-center ${cls}`}>
            <div className="text-[9px] uppercase opacity-80">{label}</div>
            <div className="text-lg font-bold">{value}</div>
        </div>
    );
}

export default VekaletDosyaSection;
